import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { apiRequest, type ApiError } from "@/shared/api";
import type {
  IUserBalancesResponse,
  IUserCryptoBalance,
  IUserFiatBalance,
} from "../interfaces/balance.interface";

type ApiQueryOptions<TData> = Omit<
  UseQueryOptions<TData, ApiError, TData, readonly unknown[]>,
  "queryKey" | "queryFn"
>;

const cryptoBalancesRequest = {
  url: "/crypto/balances",
  method: "get" as const,
};

const fiatBalancesRequest = {
  url: "/fiat/balances",
  method: "get" as const,
};

export const getUserCryptoBalances = () =>
  apiRequest<IUserCryptoBalance[]>(cryptoBalancesRequest);

export const getUserFiatBalances = () =>
  apiRequest<IUserFiatBalance[]>(fiatBalancesRequest);

export const useUserBalances = (
  options?: ApiQueryOptions<IUserBalancesResponse>
) =>
  useQuery<IUserBalancesResponse, ApiError>({
    queryKey: ["user-balances"],
    queryFn: async () => {
      const [cryptoBalances, fiatBalances] = await Promise.all([
        getUserCryptoBalances(),
        getUserFiatBalances(),
      ]);

      return {
        cryptoBalances,
        fiatBalances,
      };
    },
    ...options,
  });

const toNumeric = (value: unknown) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const toCurrencyCode = (code: unknown) =>
  typeof code === "string" && code.trim().length > 0 ? code.toUpperCase() : "";

export type WalletValueEntry = {
  id: string;
  currency: string;
  amount: number;
  amountDisplay: string;
  usdValue: number;
  type: "fiat" | "crypto";
  status?: string;
  walletAddress?: string;
  rate: number;
};

const mapFiatBalance = (balance: IUserFiatBalance): WalletValueEntry => {
  const amount = toNumeric(balance.amount);
  const rate = toNumeric(balance.rate);

  return {
    id: balance.id,
    currency: toCurrencyCode(balance.name),
    amount,
    amountDisplay: balance.amount,
    usdValue: amount * rate,
    rate,
    type: "fiat",
    status: balance.status,
  };
};

const mapCryptoBalance = (balance: IUserCryptoBalance): WalletValueEntry => {
  const amount = toNumeric(balance.amount);
  const rate = toNumeric(balance.rate);

  return {
    id: balance.id,
    currency: toCurrencyCode(balance.name),
    amount,
    amountDisplay: balance.amount,
    usdValue: amount * rate,
    rate,
    type: "crypto",
    walletAddress: balance.wallet,
  };
};

export const buildWalletEntries = (data?: IUserBalancesResponse) => {
  const fiat = data?.fiatBalances ?? [];
  const crypto = data?.cryptoBalances ?? [];

  const fiatEntries = fiat.map(mapFiatBalance);
  const cryptoEntries = crypto.map(mapCryptoBalance);

  return {
    fiatEntries,
    cryptoEntries,
    allEntries: [...fiatEntries, ...cryptoEntries],
  };
};

export const computeWalletSummary = (data?: IUserBalancesResponse) => {
  const { fiatEntries, cryptoEntries } = buildWalletEntries(data);

  const totalFiatValue = fiatEntries.reduce(
    (sum, entry) => sum + entry.usdValue,
    0
  );
  const totalCryptoValue = cryptoEntries.reduce(
    (sum, entry) => sum + entry.usdValue,
    0
  );

  return {
    totalWalletValue: totalFiatValue + totalCryptoValue,
    totalFiatValue,
    totalCryptoValue,
    hasDetailedBalances: fiatEntries.length > 0 || cryptoEntries.length > 0,
  };
};
